// Single source of truth for alert-signup copy across the four surfaces.
// All strings audited against the brand voice library, see scripts/lib/brand-guide.mjs (/brand)
// (banned: unleash, supercharge, transform, level up, hassle-free, seamless,
// world-class). Headline direction picked by founder: performance-oriented,
// "your inbox, before everyone else."

import { areaNameFor } from "@/lib/areas";

export type AlertVariant = "hero" | "properties-top" | "properties-empty" | "footer";

export interface VariantCopy {
  headline: string;
  subheadline?: string;
  cta: string;
  successHeadline: string;
  successBody?: string;
}

export const ALERT_COPY: Record<AlertVariant, VariantCopy> = {
  hero: {
    headline: "New rentals, your inbox, before everyone else.",
    subheadline: "One email when a home fits. Your number stays private.",
    cta: "Email me",
    successHeadline: "You're on the list.",
    successBody: "We'll email when a fit lands.",
  },
  "properties-top": {
    headline: "Don't see it? We'll email when it lists.",
    cta: "Email me matches",
    successHeadline: "Saved your filters.",
    successBody: "We'll email when a match lists.",
  },
  "properties-empty": {
    headline: "Nothing fits today. We'll email when one does.",
    subheadline: "Pre-qualified renters get first look.",
    cta: "Email me when one lists",
    successHeadline: "You're on the list.",
    successBody: "We'll email when a match comes online.",
  },
  footer: {
    headline: "Set detailed alerts",
    subheadline: "Pick your area, bedrooms, and budget. We'll email when something lists.",
    cta: "Email me",
    successHeadline: "You're on the list.",
    successBody: "We'll email when something fits your preferences.",
  },
};

// Mirrors the existing options used by the legacy EmailCapture form so the
// edge function's BUDGET_BUCKETS map keeps working unchanged.
export const BUDGET_OPTIONS = [
  "Under $1,000",
  "$1,000–$1,500",
  "$1,500–$2,500",
  "$2,500–$3,500",
  "$3,500+",
] as const;

export interface AreaOption {
  value: string;
  label: string;
}

/** Display label for an area slug (falls back to the slug itself). */
type TenantList = { areas: string[]; areaSlugs: string[] };

/** Display name for an area slug: the city's Locations list, then the catalog. */
export const areaLabel = (slug: string, tenant?: TenantList | null): string => areaNameFor(slug, tenant);

/**
 * Area dropdown options for a tenant, in the tenant's configured order.
 * Replaces the old static (Santa-Teresa-only) AREA_OPTIONS so each city's
 * alert form shows its own areas.
 */
export const areaOptionsForTenant = (tenant: TenantList): AreaOption[] =>
  tenant.areaSlugs.map((slug) => ({ value: slug, label: areaLabel(slug, tenant) }));

export const BEDROOM_OPTIONS = [
  { value: "1", label: "1 BR" },
  { value: "2", label: "2 BR" },
  { value: "3", label: "3 BR" },
  { value: "4", label: "4+ BR" },
] as const;
